import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowLeftRight } from 'lucide-react';
import ReservationModal from './ReservationModal';
import { useLanguage } from '../context/LanguageContext';
import { getTranslatedTour } from '../data/tourTranslations';

const TourCard = ({ tour }) => {
  const [showReservation, setShowReservation] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Get translated tour data
  const translatedTour = getTranslatedTour(tour, language);
  
  // Automatically detect if it's an offer (has original price higher than sale price)
  const isOnSale = tour.originalPrice && tour.originalPrice > tour.salePrice;

  const handleViewDetails = () => {
    navigate(`/tour/${tour.id}`);
  };

  return (
    <>
      <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group">
        {/* Image Container */}
        <div 
          className="relative overflow-hidden cursor-pointer"
          onClick={handleViewDetails}
        >
          <img
            src={tour.image}
            alt={translatedTour.title}
            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
          />
          {/* Offer Badge - Shows automatically when price is discounted */}
          {isOnSale && (
            <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded">
              {t('offer')}
            </div>
          )}
          {/* View Details Overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="text-white font-medium bg-teal-500 px-4 py-2 rounded">{t('moreInfo')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Locations */}
          <div className="flex flex-wrap gap-2 mb-2">
            {tour.locations.map((location, index) => (
              <span
                key={index}
                className="text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded"
              >
                {location}
              </span>
            ))}
          </div>

          {/* Title */}
          <h3 
            onClick={handleViewDetails}
            className="text-lg font-semibold text-gray-800 mb-3 group-hover:text-teal-500 transition-colors cursor-pointer"
          >
            {translatedTour.title}
          </h3>

          {/* Details */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{translatedTour.duration}</span>
            </div>
            <div className="flex items-center gap-1">
              <ArrowLeftRight size={14} />
              <span>{translatedTour.transport}</span>
            </div>
          </div>

          {/* Price & Button */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                {isOnSale && (
                  <span className="text-gray-400 line-through text-sm">
                    ${tour.originalPrice}
                  </span>
                )}
                <span className="text-2xl font-bold text-teal-500">
                  ${tour.salePrice}
                </span>
              </div>
              {tour.priceLabel && (
                <span className="text-xs text-gray-500">
                  {tour.priceLabel === 'por persona' ? t('perPerson') : 
                   tour.priceLabel === 'por pareja' ? t('perCouple') : 
                   tour.priceLabel === 'desde' ? t('from') : tour.priceLabel}
                </span>
              )}
              {tour.extraPrice && (
                <span className="text-xs text-orange-500 font-medium">{tour.extraPrice}</span>
              )}
            </div>
            <button 
              onClick={() => setShowReservation(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded transition-colors"
            >
              {t('bookNow')}
            </button>
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      <ReservationModal 
        isOpen={showReservation}
        onClose={() => setShowReservation(false)}
        tour={tour}
      />
    </>
  );
};

export default TourCard;
